/**
 * Innsamlinger — fundraising campaigns with a goal and their progress.  MKK module 21 «Innsamling» (owner 2026-10-29).
 *
 * Behind the `campaigns` capability (the menu item is absent without it, and every route answers 404).
 *   campaigns.read     every board role and admin: the list with each campaign's progress, and the monthly report (CSV);
 *   campaigns.manage   the treasurer and admin: write a campaign, activate it (or put it back to draft), close it.
 * Progress is computed by the server from the gifts tied to the campaign (Vipps, captured, net of refunds) and from other
 * sources' totals — a total and a count, never a donor. Closing is final and takes two clicks (TwoStep).
 *
 * Telethon (owner 2026-10-31): each campaign has a public page (`url`, …/v1/gi/<slug>) — «Kopier lenke» and «Last ned
 * QR» (a PNG drawn here by core's encoder, the same one the TV uses) — and two switches in the form: «Telethon-modus»
 * (the prayer screens stay on this campaign, with «Siste gaver» and a thank-you when the total grows) and «Vis givere
 * som vil vises» (a giver who ticked «Vis fornavnet mitt» is listed by first name; other gifts only count in the total).
 * «Telethon-modus i dag» holds the walls for the day it is ticked and ends at midnight (PM 2026-10-31).
 */
import React, { useState } from 'react';
import { can, download, errSummary, errText, fieldErrors, patch, post, type Body } from '../lib/api';
import { useLoad, useMsg, dropChanged } from '../lib/hooks';
import { longDate, isoToday } from '../lib/format';
import { Body as CardBody, Btn, Card, CopyButton, Field, FieldError, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { Campaigns, Qr } from '../core';

const PHASE_TONE: Record<string, TagTone> = { running: 'green', scheduled: 'gold', draft: 'grey', ended: 'gold', closed: 'grey' };
const phaseTag = (p: string) => <Tag tone={PHASE_TONE[p] || 'grey'}>{Campaigns.PHASE_NO[p] || p}</Tag>;
const kr = (ore: number) => Campaigns.kr(ore);

const EMPTY = { title: '', text: '', purpose: 'sadaqa', goal: '', startsOn: '', endsOn: '', status: 'draft', live: false, showDonors: false, eventId: '' };
type Form = typeof EMPTY;
const toForm = (c: Body): Form => ({ title: c.title || '', text: c.text || '', purpose: c.purpose || 'other',
  goal: Campaigns.formatKr(c.goalOre, { grouped: false }).replace(/,00$/, ''), startsOn: c.startsOn || '', endsOn: c.endsOn || '', status: c.status === 'active' ? 'active' : 'draft',
  // Telethon mode is for the day it is switched on (PM 2026-10-31): the box shows whether it holds the walls TODAY.
  live: Boolean(c.liveToday), showDonors: Boolean(c.showDonors), eventId: c.eventId || '' });

/**
 * «Last ned QR» (telethon): the campaign page's address as a PNG for a poster — drawn on a canvas by core's encoder,
 * sixteen pixels a module with the specification's quiet zone. → an error sentence, or null when the file was saved.
 */
async function downloadQr(c: Body): Promise<string | null> {
  let m: boolean[][];
  try { m = Qr.encode(String(c.url || '')); } catch { return 'Adressen er for lang til en QR-kode.'; }
  const canvas = document.createElement('canvas');
  const px = Qr.pixels(m, { scale: 16 });
  canvas.width = px; canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) return 'Nettleseren kan ikke tegne QR-koden.';
  Qr.draw(ctx, m, { scale: 16 });
  const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, 'image/png'));
  if (!blob) return 'Nettleseren kan ikke lagre QR-koden.';
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `qr-${c.slug || c.id}.png`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  return null;
}

/** The bar: how much of the goal, in kroner and per cent. Text, not colour alone, carries it. */
function Progress({ c }: { c: Body }) {
  return (
    <div>
      <progress max={100} value={Campaigns.barPercent(c.percent)} aria-label={`${c.title}: ${kr(c.raisedOre)} av ${kr(c.goalOre)}, ${c.percent} prosent`} />
      <div className="sub">{kr(c.raisedOre)} av {kr(c.goalOre)} · {c.percent} % · {c.giftCount === 1 ? '1 gave' : `${c.giftCount} gaver`}</div>
    </div>
  );
}

function CampaignForm({ f, set, errors, today, purposeLocked, events }: { f: Form; set: (f: Form) => void; errors: Record<string, string>; today: string; purposeLocked: boolean;
  events: Body[] | null }) {
  // FieldError: red under the field and a toast (2026-10-31).
  const err = (k: string) => <FieldError text={errors[k]} />;
  return (
    <>
      <div className="row">
        <Field label="Tittel" required className="field">{(id) => <><input id={id} maxLength={Campaigns.MAX_TITLE} autoComplete="off" value={f.title}
          onChange={(e) => set({ ...f, title: e.target.value })} />{err('title')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Kort tekst" className="field" note="Vises i appen og på gavesiden.">{(id, d) => <><textarea id={id} aria-describedby={d} rows={3} maxLength={Campaigns.MAX_TEXT}
          value={f.text} onChange={(e) => set({ ...f, text: e.target.value })} />{err('text')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Formål" required className="field half" note={purposeLocked ? 'Kan ikke endres: noen har gitt til innsamlingen.' : undefined}>{(id, d) => <><select id={id}
          aria-describedby={d} disabled={purposeLocked} value={f.purpose} onChange={(e) => set({ ...f, purpose: e.target.value })}>
          {Campaigns.PURPOSES.map((p) => <option key={p} value={p}>{Campaigns.PURPOSE_NO[p]}</option>)}</select>{err('purpose')}</>}</Field>
        <Field label="Mål (kr)" required className="field half" note="For eksempel 50000.">{(id, d) => <><input id={id} aria-describedby={d} inputMode="decimal"
          autoComplete="off" value={f.goal} onChange={(e) => set({ ...f, goal: e.target.value })} />{err('goalOre')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Første dag" required className="field half">{(id) => <><input id={id} type="date" value={f.startsOn}
          onChange={(e) => set({ ...f, startsOn: e.target.value })} />{err('startsOn')}</>}</Field>
        <Field label="Siste dag" required className="field half">{(id) => <><input id={id} type="date" min={today} value={f.endsOn}
          onChange={(e) => set({ ...f, endsOn: e.target.value })} />{err('endsOn')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Status" className="field half" note="Aktiv: vises i appen og på gavesiden mens perioden pågår.">{(id, d) => <><select id={id} aria-describedby={d}
          value={f.status} onChange={(e) => set({ ...f, status: e.target.value })}>
          <option value="draft">Utkast</option><option value="active">Aktiv</option></select>{err('status')}</>}</Field>
        {/* Arrangementer pro (2026-10-31): the event this is for — its page in the app offers «Gi til …». Only for those who see events. */}
        {events && <Field label="Arrangement" className="field half" note="Arrangementets side i appen viser «Gi til» innsamlingen.">{(id, d) => <><select id={id} aria-describedby={d}
          value={f.eventId} onChange={(e) => set({ ...f, eventId: e.target.value })}>
          <option value="">Ingen</option>
          {events.filter((e) => e.status !== 'cancelled' || e.id === f.eventId).map((e) => <option key={e.id} value={e.id}>{e.title_no} — {String(e.starts_at || '').slice(0, 10)}</option>)}
        </select>{err('eventId')}</>}</Field>}
      </div>
      <div className="row">
        <Field label="Telethon-modus i dag" className="field half" note="Bønneskjermene blir stående på denne innsamlingen resten av dagen, med neste bønnetid nederst, «Siste gaver» og en takk når summen øker. Slås av ved midnatt. Kan bare slås på mens innsamlingen pågår.">{(id, d) => <>
          <label htmlFor={id} className="check"><input id={id} type="checkbox" aria-describedby={d} checked={f.live}
            onChange={(e) => set({ ...f, live: e.target.checked })} /> På</label>{err('live')}</>}</Field>
        <Field label="Vis givere som vil vises" className="field half" note="En giver som krysset av for «Vis fornavnet mitt» vises med fornavn og beløp. Andre gaver vises ikke enkeltvis — de teller bare i summen.">{(id, d) => <>
          <label htmlFor={id} className="check"><input id={id} type="checkbox" aria-describedby={d} checked={f.showDonors}
            onChange={(e) => set({ ...f, showDonors: e.target.checked })} /> På</label>{err('showDonors')}</>}</Field>
      </div>
    </>
  );
}

/** The monthly report for a year: per campaign and month, Vipps and other gifts; the CSV for the accountant. */
function Report({ onClose }: { onClose: () => void }) {
  const [year, setYear] = useState(isoToday().slice(0, 4));
  const { data, error, reload } = useLoad<Body>(`/campaigns/report?year=${encodeURIComponent(year)}`, [year]);
  const [msg, say] = useMsg();
  const csv = async () => {
    const d = await download(`/v1/admin/campaigns/report.csv?year=${encodeURIComponent(year)}`, `innsamlinger-${year}.csv`);
    if (!d.ok) say(d.message || 'Kunne ikke hente CSV-filen.', 'err');
  };
  const rows: Body[] = (data && data.rows) || [];
  return (
    <CardBody>
      <div className="work-bar"><b>Månedsrapport</b>
        <Field label="År" className="field">{(id) => <input id={id} inputMode="numeric" maxLength={4} style={{ width: '6em' }} value={year}
          onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))} />}</Field>
        <span className="sp" /><Btn ghost onClick={csv}>Last ned CSV</Btn> <Btn ghost onClick={onClose}>Lukk</Btn></div>
      <Msg text={msg.text} tone={msg.tone} />
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <table className="t">
          <thead><tr><th scope="col">Innsamling</th><th scope="col">Måned</th><th scope="col">Vipps</th><th scope="col">Andre gaver</th><th scope="col">Sum</th></tr></thead>
          <tbody>{rows.length ? rows.map((r) => (
            <tr key={`${r.campaignId}-${r.month}`}>
              <td>{r.title}</td><td>{r.month || 'uten måned'}</td>
              <td>{kr(r.vippsOre)} <span className="sub">({r.vippsCount})</span></td>
              <td>{kr(r.externalOre)} <span className="sub">({r.externalCount})</span></td>
              <td><b>{kr(r.totalOre)}</b></td>
            </tr>)) : <tr><td colSpan={5}>Ingen gaver til innsamlinger i {year}.</td></tr>}</tbody>
          {rows.length > 0 && <tfoot><tr><th scope="row" colSpan={2}>Sum {data.year}</th><td>{kr(data.totals.vippsOre)}</td><td>{kr(data.totals.externalOre)}</td>
            <td><b>{kr(data.totals.totalOre)}</b></td></tr></tfoot>}
        </table>
      )}
    </CardBody>
  );
}

export function Innsamlinger() {
  const manage = can('campaigns.manage');
  const { data, error, reload } = useLoad<Body>('/campaigns', []);
  // The events to tie a campaign to (076): read only by those who may see the event list.
  const evLoad = useLoad<Body>(can('events.write') ? '/events' : null, []);
  const evList: Body[] | null = can('events.write') && evLoad.data ? (evLoad.data.items || []) : null;
  const [msg, say] = useMsg();
  const [form, setForm] = useState<{ c: Body | null; f: Form; start: Form } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(false);
  const today = (data && data.today) || isoToday();

  const save = async () => {
    if (!form || saving) return;
    const goal = Campaigns.kroner(form.f.goal);
    if (!goal.ok) { setErrors({ goalOre: goal.message }); return; }
    setSaving(true);
    try {
      const fields = { title: form.f.title, text: form.f.text.trim() || null, purpose: form.f.purpose, goalOre: goal.ore,
        startsOn: form.f.startsOn, endsOn: form.f.endsOn, status: form.f.status, live: form.f.live, showDonors: form.f.showDonors,
        ...(evList ? { eventId: form.f.eventId || null } : {}) };
      // An edit sends only what changed, with the version that was read (a change made meanwhile is 409, never overwritten).
      const r = form.c
        ? await patch(`/campaigns/${form.c.id}`, { version: form.c.version, ...Object.fromEntries(Object.entries(fields)
          .filter(([k, v]) => v !== (k === 'text' ? form.c!.text ?? null : k === 'live' ? (v ? Boolean(form.c!.liveToday) : Boolean(form.c!.live)) : k === 'showDonors' ? Boolean(form.c![k]) : form.c![k]))) })
        : await post('/campaigns', fields);
      if (!r.ok) { setErrors(fieldErrors(r.body)); say(errSummary(r.body), 'err'); if (r.body.code === 'campaign_changed') reload(); return; }
      setForm(null); say(form.c ? 'Innsamlingen er lagret.' : 'Innsamlingen er opprettet.', 'ok'); reload();
    } finally { setSaving(false); }
  };
  // «Skjul navn» (telethon): a typed name off the page and the wall; the gift stays.
  const hideName = async (c: Body, g: Body) => {
    const r = await post(`/campaigns/${c.id}/gifts/${g.id}/hide-name`, {});
    say(r.ok ? 'Navnet er skjult. Gaven vises ikke lenger i «Siste gaver», men teller i summen.' : errText(r.body), r.ok ? 'ok' : 'err');
    reload();
  };
  const close = async (c: Body) => {
    const r = await post(`/campaigns/${c.id}/close`, { version: c.version });
    say(r.ok ? 'Innsamlingen er avsluttet.' : r.body.message || errText(r.body), r.ok ? 'ok' : 'err');
    reload();
  };

  const items: Body[] = (data && data.items) || [];
  return (
    <Card title="Innsamlinger" hint="Mål for innsamling, med fremdrift regnet ut fra gavene. Givere vises aldri — bare sum og antall gaver.">
      <div className="work-bar">
        <span className="sp" />
        <Btn ghost small={false} onClick={() => setReport(!report)}>{report ? 'Skjul månedsrapport' : 'Månedsrapport'}</Btn>
        {manage && <> <Btn small={false} onClick={() => { setErrors({}); say(''); setForm({ c: null, f: EMPTY, start: EMPTY }); }}>+ Ny innsamling</Btn></>}
      </div>
      {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
      {report && <Report onClose={() => setReport(false)} />}
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <>
          <p className="vh" role="status">{items.length === 1 ? '1 innsamling' : `${items.length} innsamlinger`}</p>
          <table className="t">
            <thead><tr><th scope="col">Innsamling</th><th scope="col">Periode</th><th scope="col">Fremdrift</th><th scope="col">Status</th>
              <th scope="col"><span className="vh">Handling</span></th></tr></thead>
            <tbody>{items.length ? items.map((c) => (
              <tr key={c.id}>
                <td><b>{c.title}</b>{c.liveToday && <> <Tag tone="gold" title="Bønneskjermene står på denne innsamlingen i dag">Telethon i dag</Tag></>}
                  <div className="sub">{Campaigns.PURPOSE_NO[c.purpose] || c.purpose}{c.text ? ` — ${c.text}` : ''}</div>
                  {c.url && <div className="sub">Side: {c.url}{c.phase !== 'running' && c.phase !== 'closed' ? ' (åpner når innsamlingen pågår)' : ''}</div>}
                  {manage && (c.recentGifts || []).some((g: Body) => g.name) && <div className="sub">Siste gaver: {(c.recentGifts as Body[]).filter((g) => g.name).map((g) => (
                    <span key={g.id}>{g.name} ({kr(g.amountOre)}) <Btn ghost aria-label={`Skjul navnet ${g.name}`} onClick={() => hideName(c, g)}>Skjul navn</Btn> </span>))}</div>}</td>
                <td>{longDate(c.startsOn)} – {longDate(c.endsOn)}{c.daysLeft !== null && c.daysLeft !== undefined
                  ? <div className="sub">{c.daysLeft === 0 ? 'Siste dag i dag' : c.daysLeft === 1 ? '1 dag igjen' : `${c.daysLeft} dager igjen`}</div> : null}</td>
                <td><Progress c={c} />{c.externalCount > 0 && <div className="sub">Vipps {kr(c.vippsOre)} · andre {kr(c.externalOre)}</div>}</td>
                <td>{phaseTag(c.phase)}</td>
                <td>{c.can && c.can.edit && <Btn ghost aria-label={`Endre ${c.title}`} onClick={() => { setErrors({}); say(''); setForm({ c, f: toForm(c), start: toForm(c) }); }}>Endre</Btn>}
                  {c.can && c.can.close && <> <TwoStep label="Avslutt" confirmLabel="Klikk igjen: avslutt for godt" busyLabel="Avslutter …" onConfirm={() => close(c)} /></>}
                  {c.url && <> <CopyButton value={c.url} label="Kopier lenke" onMessage={(m) => say(m, /kopiert/i.test(m) ? 'ok' : 'warn')} />
                    {' '}<Btn ghost aria-label={`Last ned QR-kode for ${c.title}`} onClick={async () => { const e = await downloadQr(c); say(e || 'QR-koden er lastet ned.', e ? 'err' : 'ok'); }}>Last ned QR</Btn></>}</td>
              </tr>)) : <tr><td colSpan={5}>{manage ? 'Ingen innsamlinger ennå. Opprett den første med «Ny innsamling».' : 'Ingen innsamlinger ennå.'}</td></tr>}</tbody>
          </table>
        </>
      )}
      <FormDialog open={Boolean(form)} title={form && form.c ? 'Endre innsamling' : 'Ny innsamling'} dirty={Boolean(form) && JSON.stringify(form!.f) !== JSON.stringify(form!.start)}
        onClose={() => setForm(null)}>
        {form && <>
          <CampaignForm f={form.f} set={(f) => { setErrors(dropChanged(errors, form.f, f)); setForm({ ...form, f }); }} errors={errors} today={today} purposeLocked={Boolean(form.c && form.c.giftCount > 0)} events={evList} />
          <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : form.c ? 'Lagre' : 'Opprett'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </Card>
  );
}
